import { useId, useRef, useState } from "react";
import { useQuery } from "@connectrpc/connect-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { FareService, FareVersionStatus } from "@yatayat/gen/yatayat/fares/v1/fares_pb";

import { FareDraftForm } from "~/components/console/FareDraftForm";
import { FareRevisionList, inForceId } from "~/components/console/FareRevisionList";
import { FareRevisionPanel } from "~/components/console/FareRevisionPanel";
import { useStaffSessionExpiry } from "~/components/OperatorTrip";
import { QueryError } from "~/components/QueryState";
import { useI18n } from "~/lib/i18n";
import { retryTransient } from "~/lib/rpc/errors";
import { searchString } from "~/lib/search";
import { can, useStaffSession } from "~/lib/staff";
import { ui } from "~/lib/ui";

type FaresSearch = { version?: string };

// The open version lives in the URL so a drafter can send it to the approver.
export const Route = createFileRoute("/console/fares")({
  validateSearch: (search: Record<string, unknown>): FaresSearch => ({
    version: searchString(search.version),
  }),
  component: ConsoleFares,
});

function ConsoleFares() {
  const { t } = useI18n();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const session = useStaffSession().session;
  const pageHeading = useRef<HTMLHeadingElement>(null);
  const panelHeading = useRef<HTMLHeadingElement>(null);
  const draftButton = useRef<HTMLButtonElement>(null);
  const [drafting, setDrafting] = useState(false);
  const [notice, setNotice] = useState("");
  // Hidden at once, before the list refetches without them.
  const [discarded, setDiscarded] = useState<string[]>([]);
  const versionsHeading = useId();

  // Few versions exist, so one page holds them all.
  const revisions = useQuery(FareService.method.listFareRevisions, { pageSize: 100 }, { retry: retryTransient });
  const unauthenticated = useStaffSessionExpiry(revisions.error);
  const versions = (revisions.data?.versions ?? []).filter((v) => !discarded.includes(v.id));
  const inForce = inForceId(versions);

  // With none chosen, open the newest revision awaiting a step, since that is the work to do.
  const awaiting = versions.find((v) => v.status === FareVersionStatus.DRAFT || v.status === FareVersionStatus.APPROVED);
  const selected = versions.find((v) => v.id === search.version) ?? (search.version ? undefined : awaiting);
  const basedOn = selected?.basedOnId ? versions.find((v) => v.id === selected.basedOnId) : undefined;

  function select(versionId: string | undefined, focus = true) {
    void navigate({ search: { version: versionId }, replace: true, resetScroll: false }).then(() => {
      if (focus && versionId) requestAnimationFrame(() => panelHeading.current?.focus());
    });
  }

  if (!session) return null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <h1 ref={pageHeading} tabIndex={-1} className={`${ui.h1} outline-none`}>
            {t("faresTitle")}
          </h1>
          <p className={ui.muted}>{t("faresIntro")}</p>
          <p className="text-sm font-medium">{t("faresFourEyes")}</p>
        </div>
        {can.draftFares(session.role) && !drafting && (
          <button
            ref={draftButton}
            type="button"
            onClick={() => {
              setNotice("");
              setDrafting(true);
            }}
            className={ui.primary}
          >
            {t("faresDraftButton")}
          </button>
        )}
      </div>

      <div role="status">
        {notice && (
          <p className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
            <span aria-hidden="true">✓ </span>
            {notice}
          </p>
        )}
      </div>

      {drafting && (
        <FareDraftForm
          onDrafted={(version) => {
            setDrafting(false);
            setNotice(t("faresDrafted", { label: version.label }));
            select(version.id);
          }}
          onCancel={() => {
            setDrafting(false);
            requestAnimationFrame(() => draftButton.current?.focus());
          }}
        />
      )}

      <section aria-labelledby={versionsHeading} className="space-y-2">
        <h2 id={versionsHeading} className={ui.h2}>
          {t("faresVersions")}
        </h2>
        {revisions.isPending && <p className={ui.muted}>{t("loading")}</p>}
        {revisions.isError && !revisions.data && !unauthenticated && (
          <QueryError onRetry={() => void revisions.refetch()} busy={revisions.isFetching} />
        )}
        {revisions.data && versions.length === 0 && <p className={ui.empty}>{t("faresNoVersions")}</p>}
        {versions.length > 0 && <FareRevisionList versions={versions} selectedId={selected?.id} onSelect={(id) => select(id)} />}
      </section>

      {selected && (
        <FareRevisionPanel
          key={selected.id}
          version={selected}
          basedOn={basedOn}
          inForce={selected.id === inForce}
          session={session}
          headingRef={panelHeading}
          onNotice={setNotice}
          onChanged={() => {
            if (search.version !== selected.id) select(selected.id, false);
          }}
          onDiscarded={() => {
            setDiscarded((ids) => [...ids, selected.id]);
            select(undefined, false);
            requestAnimationFrame(() => pageHeading.current?.focus());
          }}
        />
      )}
    </div>
  );
}
